import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2, PlayCircle, Award } from "lucide-react";
import { api, cedi } from "../../lib/api";
import { PageHeader } from "../../components/common";
import MomoDialog from "../../components/MomoDialog";
import { MembershipCard } from "../../components/Membership";

const ACCENT = { yellow: "#F3C623", green: "#22c55e", black: "#111827" };

const EnrolSteps = ({ course, member, prereqOk }) => {
  const steps = [["Membership", member], ["Previous belt", prereqOk], ["Pay belt fee", false], ["Start learning", false]];
  return (
    <div className="dx-card p-4 mb-5 border-l-4 border-l-[#D4AF37]" data-testid="enrol-steps">
      <div className="text-sm font-semibold text-[#250E31]">Enrolling in {course.title}</div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-2">
        {steps.map(([l, ok], i) => (
          <span key={l} className={`text-xs flex items-center gap-1.5 ${ok ? "text-emerald-700 font-semibold" : "text-[#6b5a73]"}`} data-testid={`enrol-step-${i + 1}`}>
            {ok ? <CheckCircle2 className="w-4 h-4" /> : <span className="w-4 h-4 rounded-full border grid place-items-center text-[10px]">{i + 1}</span>}{l}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function Academy() {
  const [courses, setCourses] = useState([]);
  const [enr, setEnr] = useState([]);
  const [paying, setPaying] = useState(null);
  const [member, setMember] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [focus, setFocus] = useState("");
  const [params, setParams] = useSearchParams();
  const target = params.get("enrol");
  const handled = useRef("");
  const navigate = useNavigate();

  const load = () => Promise.all([api.get("/courses"), api.get("/my/enrollments"), api.get("/profile")]).then(([c, e, p]) => { setCourses(c.data); setEnr(e.data); setMember(!!p.data.membership_paid); setLoaded(true); });
  useEffect(() => { load(); }, []);

  const state = (c) => {
    const e = enr.find((x) => x.course_slug === c.slug);
    if (e) return e.status === "completed" ? ["done", e] : ["learning", e];
    if (c.prerequisite && !enr.find((x) => x.course_slug === c.prerequisite && x.status === "completed")) return ["locked"];
    return ["buy"];
  };

  const proceed = (c) => {
    const [s, e] = state(c);
    const key = `${c.slug}:${member}:${s}`;
    if (handled.current === key) return;
    handled.current = key;
    setFocus(c.slug);
    if (s === "learning") { toast.info(`You're already enrolled in ${c.title}`); return navigate(`/app/learn/${c.slug}`); }
    if (s === "done") return toast.success(`You're already certified in ${c.title}`, { description: `Certificate ${e.certificate_no}` });
    if (s === "locked") {
      const pre = courses.find((x) => x.slug === c.prerequisite);
      setFocus(pre.slug);
      return toast.warning(`Pass ${pre.title} before enrolling in ${c.title}`, { description: "We've highlighted the belt to take first." });
    }
    if (!member) return toast.info(`Step 1: activate your ${cedi(100)} membership`, { description: `After that you can pay for ${c.title}.` });
    setPaying(c);
  };

  const targetCourse = courses.find((c) => c.slug === target);
  useEffect(() => { if (loaded && targetCourse) proceed(targetCourse); }, [loaded, member, enr, target]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (focus) document.querySelector(`[data-testid=course-${focus}-card]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [focus]);
  const clearTarget = () => { if (target) setParams({}, { replace: true }); };

  return (
    <div>
      <PageHeader eyebrow="Dexterity Consult Academy" title="Lean Six Sigma certifications" subtitle={`Yellow ${cedi(500)} · Green ${cedi(1000)} · Black ${cedi(1500)} - exam and certificate included. Pass each belt to unlock the next and update your resume.`} />
      {targetCourse && <EnrolSteps course={targetCourse} member={member} prereqOk={state(targetCourse)[0] !== "locked"} />}
      {!member && <div className="mb-6"><MembershipCard compact onPaid={load} /></div>}
      <div className="grid md:grid-cols-3 gap-5">
        {courses.map((c, i) => {
          const [s, e] = state(c);
          return (
            <div key={c.slug} className={`dx-card overflow-hidden flex flex-col dx-rise dx-d${i + 1} transition-shadow ${focus === c.slug ? "ring-2 ring-[#D4AF37] shadow-xl" : ""}`} data-testid={`course-${c.slug}-card`}>
              <div className="h-2" style={{ background: ACCENT[c.belt] }} />
              <div className="p-6 flex-1 flex flex-col">
                <div className="text-xs text-[#8a7a92]">Level {c.order} · {c.code} · {c.duration}</div>
                <h3 className="font-display text-xl font-bold mt-1 text-[#250E31]">{c.title}</h3>
                <p className="text-sm text-[#6b5a73] mt-3">{c.summary}</p>
                <ul className="mt-4 space-y-1.5 text-sm text-[#4b3d52] flex-1">
                  {c.outcomes.map((o) => <li key={o} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />{o}</li>)}
                </ul>
                <div className="mt-6 flex items-center justify-between gap-2">
                  <span><span className="font-display text-2xl font-bold text-[#5B2A6E]">{cedi(c.price)}</span><span className="block text-[11px] text-[#8a7a92]">Exam & certificate included</span></span>
                  {s === "buy" && <button className="dx-btn-gold" disabled={!member} onClick={() => setPaying(c)} data-testid={`course-${c.slug}-enroll-btn`}>{member ? "Pay & enrol" : "Join first"}</button>}
                  {s === "locked" && <span className="text-xs text-[#8a7a92] flex items-center gap-1" data-testid={`course-${c.slug}-locked`}><Lock className="w-3.5 h-3.5" />Pass previous belt</span>}
                  {s === "learning" && <Link to={`/app/learn/${c.slug}`} className="dx-btn-purple" data-testid={`course-${c.slug}-continue-btn`}><PlayCircle className="w-4 h-4" />Continue</Link>}
                  {s === "done" && <Link to={`/certificate/${e.certificate_no}`} className="dx-btn-ghost" data-testid={`course-${c.slug}-certificate-btn`}><Award className="w-4 h-4" />Certificate</Link>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <MomoDialog item={paying && { title: paying.title, price: paying.price, note: "exam & certificate included" }} endpoint="/payments/momo" extra={{ course_slug: paying?.slug }}
        open={!!paying} onOpenChange={(o) => { if (!o) { setPaying(null); clearTarget(); } }}
        onPaid={() => { const slug = paying.slug; setPaying(null); navigate(`/app/learn/${slug}`); }} />
    </div>
  );
}
